// 1:1 port from age-of-mcp v3 (src/client/models/v3/quarry.ts) — the flattened
// part literals rebuilt from the original helpers + loops.
//
// v3 quarry — AUTHORED FOR THE 45° CAMERA. A genuinely RECESSED terraced
// open pit — four rock rim walls around a darker mid bench stepping down to
// a near-black pit floor, the terracing read straight down from above — with
// a timber lifting derrick leaning over the pit (mast, jib, a dressed block
// swinging on its hoist rope) as the signature vertical, dressed blocks
// stacked on the rim and rubble in the yard. Deliberately NO roof at all:
// the open pit is the icon. The quarrier stands up ON the rim, fully
// visible, working the pit below.

import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, HAZARD, STONE, STONE_DK, TIMBER } = styleV3;

const BASE = 0.12;            // foundation slab top
const RIM_H = 0.36;           // rim wall height
const RIM = BASE + RIM_H;     // rim top — the quarrier's footing

// The four rim walls around the pit, `h` tall (low for the early stages).
function rim(h: number): Part[] {
  return [
    box(0.8, h, 0.18, STONE, [0, BASE + h / 2, -0.31]),
    box(0.8, h, 0.18, STONE, [0, BASE + h / 2, 0.31]),
    box(0.18, h, 0.44, STONE, [-0.31, BASE + h / 2, 0]),
    box(0.18, h, 0.44, STONE, [0.31, BASE + h / 2, 0]),
  ];
}

// Inside the rim: the mid bench at the back and the dark pit floor in front —
// the terraces stepping down.
function pit(): Part[] {
  return [
    box(0.42, 0.16, 0.2, STONE_DK, [0, BASE + 0.08, -0.12]),
    box(0.42, 0.04, 0.22, HAZARD,  [0, BASE + 0.02, 0.11]),
    // The half-cut block still attached on the bench.
    box(0.12, 0.08, 0.1, STONE, [0.06, BASE + 0.2, -0.12], [0, 0.2, 0]),
  ];
}

// The timber lifting derrick on the north rim: mast, jib leaning over the
// pit, and a dressed block swinging on the hoist rope.
function derrick(): Part[] {
  return [
    cyl(0.03, 0.04, 0.55, 6, TIMBER, [-0.2, RIM + 0.275, -0.31]),
    box(0.05, 0.05, 0.5, TIMBER, [-0.2, RIM + 0.5, -0.08], [0.35, 0, 0]),
    cyl(0.01, 0.01, 0.4, 4, TIMBER, [-0.2, RIM + 0.21, 0.15]),
    box(0.1, 0.09, 0.1, STONE, [-0.2, RIM - 0.03, 0.15], [0, 0.3, 0]),
  ];
}

// Dressed blocks stacked on the south-east rim, rubble in the front yard.
function blocks(): Part[] {
  return [
    box(0.16, 0.12, 0.16, STONE_DK, [0.3, RIM + 0.06, 0.3]),
    box(0.13, 0.1, 0.13, STONE,     [0.3, RIM + 0.17, 0.3], [0, 0.3, 0]),
    box(0.1, 0.08, 0.1, STONE_DK,  [-0.32, BASE + 0.04, 0.42], [0.2, 0.6, 0]),
    box(0.08, 0.07, 0.08, STONE,   [-0.2, BASE + 0.035, 0.44], [0.4, 1.2, 0.2]),
  ];
}

// A quarry: a recessed terraced pit worked from the rim under a timber
// derrick — open to the sky. Grows through the four build stages.
function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.88, 0.88)];
  if (s === 1) return [slab(0.88, 0.88), ...rim(0.16), ...poles(0.82, 0.82, 0.5)];
  if (s === 2) return [slab(0.88, 0.88), ...rim(RIM_H), ...pit(), ...poles(0.82, 0.82, 0.8)];
  return [
    slab(0.88, 0.88), ...rim(RIM_H), ...pit(),
    ...derrick(), ...blocks(),
  ];
}

export const quarryV3 = defineModel({
  id: "quarry-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Quarry (v3)",
  tags: ["building","quarry","low-poly","age-of-mcp","v3"],
  summary: "Low-poly quarry.",
  description: "A worked stone pit that extracts stone on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0.15, 0.48, -0.31], facing: 0 },
  ],
  interactions: [
    { id: "cut", label: "Cutting stone", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
